import { fetchWithAuth } from "../utils/fetchWithAuth";
import stepByStepManifest from "../shared/featureWorkflows/stepByStepManifest";

class ActionPlanWorkflowService {
  constructor() {
    this.WORKFLOW_STEPS = stepByStepManifest.steps.map(step => ({
      ...step,
      status: "pending",
    }));

    this.workflowResults = {};
    this.csvToolsCache = [];
    this.csvProgramsCache = [];
    this.csvMethodsCache = [];
  }

  getSteps() {
    return [...this.WORKFLOW_STEPS];
  }

  resetWorkflow() {
    this.workflowResults = {};
    this.WORKFLOW_STEPS.forEach(step => {
      step.status = "pending";
    });
  }

  async executeWorkflow(params, callbacks) {
    const { designChallenge, briefData, strategyData, csvData, productStrategy, competitorAnalysis, nextStepContext, planId } = params;
    const { onStepStart, onStepComplete, onComplete, onError, onInsufficientCredits } = callbacks;

    // Check credits before starting
    try {
      const creditCheck = await fetchWithAuth('/api/credits/check?action=action-plan');
      if (creditCheck.ok) {
        const creditData = await creditCheck.json();
        if (!creditData.allowed) {
          if (onInsufficientCredits) onInsufficientCredits();
          else if (onError) onError(Object.assign(new Error('Insufficient credits'), { type: 'insufficient_credits' }));
          return null;
        }
      }
    } catch (_) {}

    // Check if competitor analysis has meaningful data that can replace research planning
    const hasCompetitorAnalysisData = this.hasValidCompetitorAnalysis(competitorAnalysis);

    console.log("📋 ACTION PLAN WORKFLOW: Starting", {
      designChallenge: designChallenge?.substring(0, 100) + "...",
      hasBriefData: !!briefData,
      hasStrategyData: !!strategyData,
      hasCsvData: !!csvData,
      hasProductStrategy: !!productStrategy,
      hasCompetitorAnalysis: !!competitorAnalysis,
      usingCompetitorAnalysisAsResearch: hasCompetitorAnalysisData,
    });

    // Store strategyData for use in workflow steps
    this.strategyData = strategyData;
    // Store product strategy, competitor analysis, and next step context for program generation
    this.productStrategy = productStrategy;
    this.competitorAnalysis = competitorAnalysis;

    // Derive next step context: prefer explicit CTA-triggered context, then fall back to
    // the first recommended_next_steps entry from the product strategy if available
    if (nextStepContext) {
      this.nextStepContext = nextStepContext;
    } else if (productStrategy?.strategic_guidance?.recommended_next_steps?.length > 0) {
      const firstStep = productStrategy.strategic_guidance.recommended_next_steps[0];
      if (firstStep?.ladder_rung) {
        this.nextStepContext = {
          action: firstStep.action || "",
          ladder_rung: firstStep.ladder_rung,
          focus_topics: firstStep.focus_topics || [],
          rationale: firstStep.rationale || "",
        };
        console.log("📋 ACTION PLAN: Derived nextStepContext from product strategy:", this.nextStepContext);
      } else {
        this.nextStepContext = null;
      }
    } else {
      this.nextStepContext = null;
    }

    if (csvData) {
      this.csvToolsCache = csvData.tools || [];
      this.csvProgramsCache = csvData.programsOfWork || [];
      this.csvMethodsCache = csvData.methods || [];
    }

    try {
      const steps = this.getSteps();
      let researchResults;

      if (hasCompetitorAnalysisData) {
        // Skip research planning step - use competitor analysis as research context
        console.log("📊 Using existing Competitor Analysis as research context (skipping fresh research)");
        if (onStepStart) onStepStart(0, { ...steps[0], agent: "UX Research Lead", note: "Using existing Competitor Analysis" });
        
        researchResults = this.transformCompetitorAnalysisToResearch(competitorAnalysis);
        console.log("📝 Competitor Analysis transformed to research context");
        if (onStepComplete)
          onStepComplete(0, { ...steps[0], status: "completed", skipped: false, usedExisting: true }, researchResults);
      } else {
        // No competitor analysis - run fresh research planning
        if (onStepStart) onStepStart(0, { ...steps[0], agent: "UX Research Lead" });

        researchResults = await this.executeContextualResearch(
          designChallenge,
          briefData,
          planId,
        );
        console.log("📝 Contextual Research completed by UX Research Lead");
        if (onStepComplete)
          onStepComplete(0, { ...steps[0], status: "completed" }, researchResults);
      }

      if (onStepStart) onStepStart(1, { ...steps[1], agent: "UX Design Lead" });

      const programsResults = await this.executeCustomProgramGeneration(
        designChallenge,
        briefData,
        researchResults,
        planId,
      );
      console.log("📝 Custom Programs completed by UX Design Lead");
      if (onStepComplete)
        onStepComplete(1, { ...steps[1], status: "completed" }, programsResults);

      const processedPrograms = await this.processAndEnhancePrograms(designChallenge);

      const finalReport = {
        contextualResearch: researchResults,
        customPrograms: programsResults,
        finalProgramSequence: processedPrograms,
        selectedPrograms: processedPrograms,
        autonomousPrograms: programsResults.autonomousPrograms || [],
        interconnectedTools: programsResults.interconnectedTools || [],
        programSequenceStrategy: programsResults.program_sequence_strategy || "",
        solutionMethodology: programsResults.solution_methodology || "",
        researchApproach: researchResults.researchMethods,
        dataCollection: researchResults.dataCollectionApproaches,
        totalDuration: processedPrograms.reduce(
          (total, program) => total + (program.totalProgramLength || 0),
          0,
        ) || 120,
        isActionPlan: true,
        workflowType: "action_plan",
        metadata: {
          generatedAt: new Date().toISOString(),
        },
      };

      if (onComplete) onComplete(finalReport);

      // Deduct credits after successful completion
      try {
        const deductRes = await fetchWithAuth('/api/credits/deduct', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'action-plan', planId }),
        });
        if (deductRes.ok) {
          const deductData = await deductRes.json();
          if (!deductData.success && onInsufficientCredits) onInsufficientCredits();
        }
      } catch (_) {}

      return finalReport;

    } catch (error) {
      console.error("❌ Action Plan workflow failed:", error);
      if (onError) onError(error);
      throw error;
    }
  }

  async executeContextualResearch(designChallenge, briefData, planId = null) {
    try {
      const result = await fetchWithAuth(
        "/autonomous-ai/autonomous-research-planning",
        {
          method: "POST",
          body: {
            design_challenge: designChallenge,
            deep_analysis: briefData,
            research_type: "contextual_adaptive",
            use_csv_as_knowledge: false,
            plan_id: planId,
            agentId: "UX_RESEARCH",
            context: {
              source: "action_plan_workflow",
              step: "contextual_research",
            },
          },
        },
      );

      if (!result.ok) {
        throw new Error(`Contextual research failed with status ${result.status}`);
      }

      const researchData = await result.json();

      this.workflowResults.contextualResearch = {
        ...researchData,
        researchMethods: researchData.research_methods || [],
        dataCollectionApproaches: researchData.data_collection || [],
        validationStrategies: researchData.validation_strategies || [],
      };

      return this.workflowResults.contextualResearch;
    } catch (error) {
      console.error("Contextual research step failed:", error);
      throw error;
    }
  }

  async executeCustomProgramGeneration(designChallenge, briefData, researchPlan, planId = null) {
    try {
      const result = await fetchWithAuth(
        "/autonomous-ai/autonomous-program-generation",
        {
          method: "POST",
          body: {
            design_challenge: designChallenge,
            deep_analysis: briefData,
            research_plan: researchPlan,
            generation_type: "fully_custom",
            csv_knowledge_base: true,
            csvData: {
              tools: this.csvToolsCache,
              methods: this.csvMethodsCache,
              programsOfWork: this.csvProgramsCache,
            },
            plan_id: planId,
            agentId: "UX_DESIGN_LEAD",
            context: { source: "action_plan_workflow", step: "custom_programs" },
            product_strategy: this.productStrategy || null,
            competitor_analysis: this.competitorAnalysis || null,
            next_step_context: this.nextStepContext || null,
          },
        },
      );

      if (!result.ok) {
        throw new Error(`Custom program generation failed with status ${result.status}`);
      }

      const programsData = await result.json();

      this.workflowResults.customPrograms = {
        ...programsData,
        autonomousPrograms: programsData.autonomous_programs || [],
        interconnectedTools: programsData.interconnected_tools || [],
        adaptiveMethodologies: programsData.adaptive_methodologies || [],
        innovativeApproaches: programsData.innovative_approaches || [],
      };

      return this.workflowResults.customPrograms;
    } catch (error) {
      console.error("Custom program generation step failed:", error);
      throw error;
    }
  }

  async processAndEnhancePrograms(designChallenge) {
    console.log("🔧 Starting heavy program processing and AI enhancement...");

    if (!this.workflowResults.customPrograms) {
      throw new Error("No custom programs to process");
    }

    const autonomousPrograms = this.workflowResults.customPrograms.autonomousPrograms || [];
    const interconnectedTools = this.workflowResults.customPrograms.interconnectedTools || [];

    const allToolsGlobal = new Set();
    const toolStageMap = new Map();
    const cleanedInterconnectedTools = interconnectedTools.filter(tool => {
      const normalizedName = tool.tool.toLowerCase().trim();
      if (allToolsGlobal.has(normalizedName)) {
        console.log(`🧹 Removed duplicate tool globally: "${tool.tool}"`);
        return false;
      }
      allToolsGlobal.add(normalizedName);
      toolStageMap.set(normalizedName, tool.stage_assignment);
      return true;
    });

    console.log(`🧹 STRICT tool deduplication: ${interconnectedTools.length} → ${cleanedInterconnectedTools.length} tools`);

    const processedPrograms = await Promise.all(autonomousPrograms.map(async (program, index) => {
      console.log(`🔄 Processing program ${index + 1}/${autonomousPrograms.length}: ${program.program_of_work}`);

      const stages = program.custom_stages || program.stages || ["Implementation"];

      const toolsByStage = this.createToolsByStageFromCustomProgram(
        program,
        cleanedInterconnectedTools,
        stages,
      );

      const calculatedDuration = Object.values(toolsByStage).reduce(
        (total, stage) => total + (parseInt(stage.totalDuration, 10) || 0),
        0,
      );
      const finalDuration = calculatedDuration > 0 ? calculatedDuration : parseInt(program.duration_estimate, 10) || 40;

      const deliverables = program.deliverables_feed_forward
        ? Array.isArray(program.deliverables_feed_forward)
          ? program.deliverables_feed_forward
          : program.deliverables_feed_forward.split(",").map((d) => d.trim())
        : [];

      const programDeliverables = program.program_deliverables 
        ? Array.isArray(program.program_deliverables)
          ? program.program_deliverables
          : program.program_deliverables.split(",").map((d) => d.trim())
        : deliverables;

      const programStakeholders = program.program_stakeholders
        ? Array.isArray(program.program_stakeholders) 
          ? program.program_stakeholders
          : program.program_stakeholders.split(",").map((s) => s.trim())
        : [];

      let finalDeliverables = programDeliverables.length > 0 ? programDeliverables : deliverables;
      let finalStakeholders = programStakeholders;

      if (finalDeliverables.length === 0 || finalStakeholders.length === 0) {
        try {
          if (finalDeliverables.length === 0) {
            const deliverablesResponse = await this.generateProgramDeliverables(
              designChallenge, 
              program, 
              toolsByStage
            );
            if (deliverablesResponse && deliverablesResponse.length > 0) {
              finalDeliverables = deliverablesResponse;
            }
          }

          if (finalStakeholders.length === 0) {
            const stakeholdersResponse = await this.generateProgramStakeholders(
              designChallenge,
              program,
              toolsByStage
            );
            if (stakeholdersResponse && stakeholdersResponse.length > 0) {
              finalStakeholders = stakeholdersResponse;
            }
          }
        } catch (error) {
          console.warn(`⚠️ Failed to generate missing deliverables/stakeholders for ${program.program_of_work}:`, error);
        }
      }

      let programMethods;
      try {
        programMethods = this.extractMethodsFromProgram(program);
      } catch (methodError) {
        console.error(`❌ CRITICAL: AI failed to provide methods for ${program.program_of_work}:`, methodError);
        throw new Error(`Autonomous AI failed to provide methodology for program: ${program.program_of_work}. Fix the AI prompt.`);
      }

      return {
        program_of_work: program.program_of_work,
        phase: program.phase,
        goal: program.goal || program.description,
        description: program.description,
        totalProgramLength: finalDuration,
        isIncluded: true,
        methods: programMethods,
        Stages: stages,
        focus: [],
        expectedDeliverables: finalDeliverables,
        stakeholdersInvolvement: finalStakeholders.length > 0 ? finalStakeholders.join("\n") : "",
        toolsByStage: toolsByStage,
        score: 0,
        similarity_score: 0,
        programType: program.program_type || "Strategic Phase",
        prerequisites: program.prerequisites || [],
        deliverablesFeedForward: program.deliverables_feed_forward || [],
        sequencePosition: index + 1,
        isAutonomous: true,
      };
    }));

    this.workflowResults.processedPrograms = processedPrograms;
    console.log(`✅ Completed heavy processing of ${processedPrograms.length} programs`);
    return processedPrograms;
  }

  createToolsByStageFromCustomProgram(program, interconnectedTools, stages) {
    console.log(`🤖 Creating toolsByStage for program: ${program.program_of_work}`);

    const programTools = interconnectedTools.filter((tool) => {
      return tool.program_assignment === program.program_of_work;
    });

    console.log(`🤖 Found ${programTools.length} tools for program: ${program.program_of_work}`);
    
    // DEBUG: Log all unique stage_assignment values from tools
    const uniqueStageAssignments = [...new Set(programTools.map(t => t.stage_assignment))];
    console.log(`🔍 DEBUG - stage_assignment values from tools:`, JSON.stringify(uniqueStageAssignments));
    console.log(`🔍 DEBUG - stages array to match against:`, JSON.stringify(stages));
    
    // DEBUG: Find which stage_assignments don't have an exact match in stages
    const unmatchedAssignments = uniqueStageAssignments.filter(sa => !stages.includes(sa));
    if (unmatchedAssignments.length > 0) {
      console.log(`⚠️ DEBUG - stage_assignments with NO exact match in stages:`, JSON.stringify(unmatchedAssignments));
    }

    if (!programTools || programTools.length === 0) {
      const emptyToolsByStage = {};
      stages.forEach((stage) => {
        emptyToolsByStage[stage] = { tools: [], totalDuration: 0, isAutonomous: true };
      });
      return emptyToolsByStage;
    }

    const toolsByStage = {};
    let totalAssigned = 0;

    stages.forEach((stage) => {
      const stageTools = programTools.filter((tool) => {
        // FIX: Only use exact matching - fuzzy matching was causing tools to be assigned to wrong stages
        // The backend already sets stage_assignment correctly, so we trust it
        const exactMatch = tool.stage_assignment === stage;
        const caseInsensitiveMatch = tool.stage_assignment?.toLowerCase() === stage.toLowerCase();
        
        return exactMatch || caseInsensitiveMatch;
      });

      totalAssigned += stageTools.length;

      const totalDuration = stageTools.reduce(
        (sum, tool) => sum + (parseInt(tool.duration, 10) || 0),
        0,
      );

      toolsByStage[stage] = {
        tools: stageTools.map((tool, index) => ({
          ...tool,
          id: `ai-${Date.now()}-${index}-${tool.tool.toLowerCase().replace(/\s+/g, "-")}`,
          related_stage: stage,
          isAutonomous: true,
        })),
        totalDuration: totalDuration,
        isAutonomous: true,
      };
    });

    console.log(`🔒 Assigned ${totalAssigned} tools across ${stages.length} stages for ${program.program_of_work}`);
    
    // DEBUG: Log which stages ended up empty
    const emptyStages = stages.filter(s => toolsByStage[s]?.tools?.length === 0);
    if (emptyStages.length > 0) {
      console.log(`⚠️ DEBUG - Stages with ZERO tools:`, JSON.stringify(emptyStages));
      // Log tools that weren't assigned to any stage
      const assignedStageNames = new Set(stages);
      const unassignedTools = programTools.filter(t => !assignedStageNames.has(t.stage_assignment) && !stages.some(s => s.toLowerCase() === t.stage_assignment?.toLowerCase()));
      if (unassignedTools.length > 0) {
        console.log(`⚠️ DEBUG - Unassigned tools (${unassignedTools.length}):`, JSON.stringify(unassignedTools.map(t => ({ tool: t.tool, stage_assignment: t.stage_assignment }))));
      }
    }

    return toolsByStage;
  }

  extractMethodsFromProgram(program) {
    if (program.methodology) {
      if (Array.isArray(program.methodology)) {
        return program.methodology;
      }
      return [program.methodology];
    }
    
    if (program.methods) {
      if (Array.isArray(program.methods)) {
        return program.methods;
      }
      return [program.methods];
    }

    if (program.adaptive_methodologies && program.adaptive_methodologies.length > 0) {
      return program.adaptive_methodologies;
    }

    throw new Error(`No methodology found for program: ${program.program_of_work}`);
  }

  async generateProgramDeliverables(designChallenge, program, toolsByStage) {
    try {
      const result = await fetchWithAuth("/autonomous-ai/generate-program-deliverables", {
        method: "POST",
        body: {
          design_challenge: designChallenge,
          program_name: program.program_of_work,
          program_description: program.description,
          stages: Object.keys(toolsByStage),
          tools: Object.values(toolsByStage).flatMap(s => s.tools.map(t => t.tool)),
        },
      });

      if (result.ok) {
        const data = await result.json();
        return data.deliverables || [];
      }
      return [];
    } catch (error) {
      console.warn("Failed to generate deliverables:", error);
      return [];
    }
  }

  async generateProgramStakeholders(designChallenge, program, toolsByStage) {
    try {
      const result = await fetchWithAuth("/autonomous-ai/generate-program-stakeholders", {
        method: "POST",
        body: {
          design_challenge: designChallenge,
          program_name: program.program_of_work,
          program_description: program.description,
        },
      });

      if (result.ok) {
        const data = await result.json();
        return data.stakeholders || [];
      }
      return [];
    } catch (error) {
      console.warn("Failed to generate stakeholders:", error);
      return [];
    }
  }

  semanticStageMatch(toolStage, programStage) {
    if (!toolStage || !programStage) return false;

    const normalizeStage = (stage) => stage.toLowerCase().trim();
    const tool = normalizeStage(toolStage);
    const program = normalizeStage(programStage);

    if (tool === program) return true;

    const programWords = program.split(" ");
    const toolWords = tool.split(" ");

    return programWords.some((pWord) =>
      toolWords.some(
        (tWord) =>
          pWord.length > 3 &&
          tWord.length > 3 &&
          (pWord.includes(tWord) || tWord.includes(pWord)),
      ),
    );
  }

  fuzzyMatch(str1, str2, threshold = 0.6) {
    if (!str1 || !str2) return false;

    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();

    if (s1 === s2) return true;
    if (s1.includes(s2) || s2.includes(s1)) return true;

    const maxLen = Math.max(s1.length, s2.length);
    if (maxLen === 0) return true;

    let matches = 0;
    const shorter = s1.length <= s2.length ? s1 : s2;
    const longer = s1.length > s2.length ? s1 : s2;

    for (let i = 0; i < shorter.length; i++) {
      if (longer.includes(shorter[i])) {
        matches++;
      }
    }

    return matches / maxLen >= threshold;
  }

  hasValidCompetitorAnalysis(competitorAnalysis) {
    if (!competitorAnalysis) return false;
    
    // Check if we have meaningful research data from competitor analysis
    const hasMarketIntelligence = competitorAnalysis.market_intelligence && 
      (typeof competitorAnalysis.market_intelligence === 'object' || 
       (typeof competitorAnalysis.market_intelligence === 'string' && competitorAnalysis.market_intelligence.length > 50));
    
    const hasStrategicRecommendations = competitorAnalysis.strategic_recommendations && 
      Array.isArray(competitorAnalysis.strategic_recommendations) && 
      competitorAnalysis.strategic_recommendations.length > 0;
    
    const hasOpenQuestions = competitorAnalysis.open_questions && 
      Array.isArray(competitorAnalysis.open_questions) && 
      competitorAnalysis.open_questions.length > 0;
    
    const hasOpportunityAnalysis = competitorAnalysis.opportunity_analysis && 
      (typeof competitorAnalysis.opportunity_analysis === 'object' ||
       (typeof competitorAnalysis.opportunity_analysis === 'string' && competitorAnalysis.opportunity_analysis.length > 50));

    // Need at least 2 meaningful fields to use as research replacement
    const meaningfulFields = [hasMarketIntelligence, hasStrategicRecommendations, hasOpenQuestions, hasOpportunityAnalysis];
    const validFieldCount = meaningfulFields.filter(Boolean).length;
    
    console.log("📊 Competitor Analysis validation:", {
      hasMarketIntelligence,
      hasStrategicRecommendations,
      hasOpenQuestions,
      hasOpportunityAnalysis,
      validFieldCount,
      isValid: validFieldCount >= 2
    });
    
    return validFieldCount >= 2;
  }

  transformCompetitorAnalysisToResearch(competitorAnalysis) {
    const researchMethods = [];
    const dataCollectionApproaches = [];
    const validationStrategies = [];
    
    // Extract research methods from market intelligence
    if (competitorAnalysis.market_intelligence) {
      researchMethods.push("Competitive Market Analysis - Based on existing competitor analysis data");
      dataCollectionApproaches.push("Market intelligence gathering from competitor landscape review");
    }
    
    // Extract validation from strategic recommendations
    if (competitorAnalysis.strategic_recommendations && Array.isArray(competitorAnalysis.strategic_recommendations)) {
      competitorAnalysis.strategic_recommendations.forEach((rec, idx) => {
        if (idx < 5) { // Limit to top 5
          const recText = typeof rec === 'string' ? rec : (rec.recommendation || rec.action || JSON.stringify(rec));
          validationStrategies.push(recText);
        }
      });
    }
    
    // Extract from open questions as research gaps
    if (competitorAnalysis.open_questions && Array.isArray(competitorAnalysis.open_questions)) {
      competitorAnalysis.open_questions.forEach((q, idx) => {
        if (idx < 5) {
          const questionText = typeof q === 'string' ? q : (q.question || q.text || JSON.stringify(q));
          researchMethods.push(`Investigate: ${questionText}`);
        }
      });
    }
    
    // Build business intelligence from opportunity analysis
    if (competitorAnalysis.opportunity_analysis) {
      dataCollectionApproaches.push("Opportunity analysis from competitive positioning review");
    }
    
    // Add differentiation opportunities as research focus
    if (competitorAnalysis.differentiation_opportunities && Array.isArray(competitorAnalysis.differentiation_opportunities)) {
      competitorAnalysis.differentiation_opportunities.forEach((opp, idx) => {
        if (idx < 3) {
          const oppText = typeof opp === 'string' ? opp : (opp.opportunity || opp.description || JSON.stringify(opp));
          researchMethods.push(`Differentiation research: ${oppText}`);
        }
      });
    }

    return {
      source: "competitor_analysis",
      usedExistingAnalysis: true,
      researchMethods: researchMethods.length > 0 ? researchMethods : ["Competitive landscape analysis from existing data"],
      dataCollectionApproaches: dataCollectionApproaches.length > 0 ? dataCollectionApproaches : ["Market intelligence from competitor review"],
      validationStrategies: validationStrategies.length > 0 ? validationStrategies : ["Strategic recommendations from competitor analysis"],
      business_intelligence_methods: ["Derived from existing competitor analysis report"],
      strategic_stakeholder_research: ["Informed by competitive positioning insights"],
      customer_experience_programs: ["Based on market gaps and differentiation opportunities"],
      market_validation_approaches: ["Grounded in existing market research data"],
      progressive_validation_gates: ["Aligned with strategic recommendations from competitor analysis"],
      business_question_mapping: competitorAnalysis.open_questions || [],
      strategic_risk_mitigation: competitorAnalysis.strategic_recommendations || [],
      rawCompetitorAnalysis: competitorAnalysis,
    };
  }
}

export default ActionPlanWorkflowService;
